import { DeviceInfoDeviceTypeEnum } from "@my-helpers/common";

export interface WebDeviceInfo {
    deviceId: string;
    deviceType: DeviceInfoDeviceTypeEnum;
    platform: string;
    deviceModel: string;
    osInfo: {
      name: string;
      version: string;
    };
    browser: {
      name: string;
      version: string;
      userAgent: string;
    };
    system: {
      language: string;
      timezone: string;
    };
    screen: {
      width: number;
      height: number;
      colorDepth: number;
      pixelRatio: number;
    };
    capabilities: {
      hardwareConcurrency: number;
      maxTouchPoints: number;
      deviceMemory?: number;
      connection?: string;
      cookieEnabled: boolean;
      javaEnabled: boolean;
    };
    app: {
      version: string;
    };
  }
  
  // webDeviceInfo.ts - Web browser device info utilities
  class WebDeviceInfoCollector {
    private static fingerprint: string | null = null;
  
    /**
     * Generate a stable browser fingerprint
     */
    private static generateFingerprint(): string {
      if (this.fingerprint) {
        return this.fingerprint;
      }
  
      const fingerprintData = [
        navigator.userAgent,
        navigator.language,
        navigator.languages?.join(',') || '',
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        screen.width + 'x' + screen.height,
        screen.colorDepth,
        window.devicePixelRatio,
        navigator.hardwareConcurrency || 0,
        navigator.maxTouchPoints || 0,
        navigator.cookieEnabled,
        typeof navigator.javaEnabled === 'function' ? navigator.javaEnabled() : false,
        this.getCanvasFingerprint()
      ].join('|');
  
      // Simple but effective hash
      let hash = 0;
      for (let i = 0; i < fingerprintData.length; i++) {
        const char = fingerprintData.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
  
      this.fingerprint = Math.abs(hash).toString(36);
      return this.fingerprint;
    }
  
    /**
     * Generate canvas fingerprint for additional entropy
     */
    private static getCanvasFingerprint(): string {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return 'no-canvas';
  
        canvas.width = 200;
        canvas.height = 50;
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillStyle = '#f60';
        ctx.fillRect(10, 1, 62, 20);
        ctx.fillStyle = '#069';
        ctx.fillText('BrowserID', 2, 15);
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.fillText('BrowserID', 4, 17);
  
        return canvas.toDataURL().slice(-50); // Last 50 chars for uniqueness
      } catch {
        return 'canvas-error';
      }
    }
  
    /**
     * Parse browser information from user agent
     */
    private static getBrowserInfo(): { name: string; version: string } {
      const userAgent = navigator.userAgent;
  
      // Chrome
      if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
        const version = userAgent.match(/Chrome\/([\d.]+)/)?.[1] || 'Unknown';
        return { name: 'Chrome', version };
      }
  
      // Edge
      if (userAgent.includes('Edg')) {
        const version = userAgent.match(/Edg\/([\d.]+)/)?.[1] || 'Unknown';
        return { name: 'Edge', version };
      }
  
      // Firefox
      if (userAgent.includes('Firefox')) {
        const version = userAgent.match(/Firefox\/([\d.]+)/)?.[1] || 'Unknown';
        return { name: 'Firefox', version };
      }
  
      // Safari
      if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
        const version = userAgent.match(/Version\/([\d.]+)/)?.[1] || 'Unknown';
        return { name: 'Safari', version };
      }
  
      return { name: 'Unknown', version: 'Unknown' };
    }
  
    /**
     * Get platform information optimized for web
     */
    private static getPlatform(): string {
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
  
      if (userAgent.includes('Windows') || platform.includes('Win')) return 'Windows';
      if (userAgent.includes('Macintosh') || platform.includes('Mac')) return 'macOS';
      if (userAgent.includes('Linux') || platform.includes('Linux')) return 'Linux';
      if (userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS';
      if (userAgent.includes('Android')) return 'Android';
  
      return platform || 'Unknown';
    }
  
    /**
     * Get OS information for web browsers
     */
    private static getOSInfo(): { name: string; version: string } {
      const userAgent = navigator.userAgent;
      
      if (userAgent.includes('Windows NT')) {
        const version = userAgent.match(/Windows NT ([\d.]+)/)?.[1] || 'Unknown';
        return { name: 'Windows', version };
      }
      
      if (userAgent.includes('Mac OS X')) {
        const version = userAgent.match(/Mac OS X ([\d._]+)/)?.[1]?.replace(/_/g, '.') || 'Unknown';
        return { name: 'macOS', version };
      }
      
      if (userAgent.includes('iPhone OS')) {
        const version = userAgent.match(/iPhone OS ([\d._]+)/)?.[1]?.replace(/_/g, '.') || 'Unknown';
        return { name: 'iOS', version };
      }
      
      if (userAgent.includes('Android')) {
        const version = userAgent.match(/Android ([\d.]+)/)?.[1] || 'Unknown';
        return { name: 'Android', version };
      }
      
      if (userAgent.includes('Linux')) {
        return { name: 'Linux', version: 'Unknown' };
      }
  
      return { name: 'Unknown', version: 'Unknown' };
    }
  
    /**
     * Get device model information for web (browser-focused)
     */
    private static getDeviceModel(): string {
      const browser = this.getBrowserInfo();
      return `${browser.name} ${browser.version}`;
    }
  
    /**
     * Get connection information if available
     */
    private static getConnectionType(): string | undefined {
      const nav = navigator as any;
      const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
      return connection?.effectiveType || connection?.type;
    }
  
    /**
     * Get timezone information
     */
    private static getTimezone(): string {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch {
        return `UTC${-new Date().getTimezoneOffset() / 60}`;
      }
    }
  
    /**
     * Collect all web device information
     */
    static collect(): WebDeviceInfo {
      const browser = this.getBrowserInfo();
      const osInfo = this.getOSInfo();
  
      return {
        deviceId: this.generateFingerprint(),
        deviceType: DeviceInfoDeviceTypeEnum.WebBrowser,
        platform: this.getPlatform(),
        deviceModel: this.getDeviceModel(),
        osInfo,
        browser: {
          name: browser.name,
          version: browser.version,
          userAgent: navigator.userAgent
        },
        system: {
          language: navigator.language,
          timezone: this.getTimezone()
        },
        screen: {
          width: screen.width,
          height: screen.height,
          colorDepth: screen.colorDepth,
          pixelRatio: window.devicePixelRatio || 1
        },
        capabilities: {
          hardwareConcurrency: navigator.hardwareConcurrency || 1,
          maxTouchPoints: navigator.maxTouchPoints || 0,
          deviceMemory: (navigator as any).deviceMemory,
          connection: this.getConnectionType(),
          cookieEnabled: navigator.cookieEnabled,
          javaEnabled: typeof navigator.javaEnabled === 'function' ? navigator.javaEnabled() : false
        },
        app: {
          version: import.meta.env.VITE_VERSION || '1.0.0'
        }
      };
    }
  
    /**
     * Get minimal device info for lightweight usage
     */
    static collectBasic(): Pick<WebDeviceInfo, 'deviceId' | 'deviceType' | 'platform' | 'deviceModel' | 'osInfo'> {
      return {
        deviceId: this.generateFingerprint(),
        deviceType: DeviceInfoDeviceTypeEnum.WebBrowser,
        platform: this.getPlatform(),
        deviceModel: this.getDeviceModel(),
        osInfo: this.getOSInfo()
      };
    }
  }
  
  // Public API
  export const getDeviceInfo = (): WebDeviceInfo => {
    return WebDeviceInfoCollector.collect();
  };
  
  // For backward compatibility with existing code
  export const generateBrowserFingerprint = (): string => {
    return WebDeviceInfoCollector.collect().deviceId;
  };
  